import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import api, { rupee, formatApiErrorDetail } from "@/lib/api";
import { Check, Loader2, Calculator } from "lucide-react";

const TYPES = [
  { v: "jewellery", l: "Jewellery" }, { v: "clinic", l: "Clinic" }, { v: "restaurant", l: "Restaurant" },
  { v: "electronics", l: "Electronics" }, { v: "clothing", l: "Clothing" }, { v: "interior", l: "Interior Design" },
  { v: "service", l: "Service Business" }, { v: "other", l: "Other" },
];
const FEATURES = [
  { k: "catalogue", l: "Product Catalogue" }, { k: "ecommerce", l: "E-commerce / Online Payments" },
  { k: "booking", l: "Appointment Booking" }, { k: "dashboard", l: "Admin Dashboard" },
  { k: "content_support", l: "Content Writing Support" }, { k: "maintenance", l: "Monthly Maintenance" },
];

export default function EstimateCalculator() {
  const [step, setStep] = useState(0);
  const [sel, setSel] = useState({ business_type: "jewellery", pages: 5, catalogue: false, ecommerce: false, booking: false, dashboard: false, content_support: false, maintenance: false });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lead, setLead] = useState({ name: "", phone: "", consent: false });
  const [submitted, setSubmitted] = useState(false);

  const calc = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/public/estimate", sel);
      setResult(data);
      setStep(3);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const submitLead = async () => {
    if (!lead.consent) return toast.error("Please provide consent to be contacted.");
    try {
      await api.post("/public/leads", {
        name: lead.name, phone: lead.phone, consent: true, source: "estimate",
        business_name: "", category: sel.business_type,
        features: FEATURES.filter((f) => sel[f.k]).map((f) => f.l),
        estimate: result, referral_code: localStorage.getItem("nova_ref") || null,
      });
      setSubmitted(true);
      toast.success("Enquiry sent! We'll reach out with a confirmed quote.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="glass-strong rounded-3xl p-6 sm:p-10 max-w-3xl mx-auto" data-testid="estimate-calculator">
      <div className="flex items-center gap-3 mb-6">
        <span className="grid place-items-center w-11 h-11 rounded-xl bg-violet-500/20 text-violet-300"><Calculator className="w-5 h-5" /></span>
        <div>
          <h3 className="font-display text-xl font-bold text-white">Build Your Website Estimate</h3>
          <p className="text-sm text-slate-400">Indicative pricing in seconds.</p>
        </div>
      </div>

      <div className="flex gap-1.5 mb-8">
        {[0,1,2,3].map((i)=>(<div key={i} className={`h-1.5 flex-1 rounded-full ${step>=i?"bg-violet-500":"bg-white/10"}`} />))}
      </div>

      <AnimatePresence mode="wait">
        {step === 0 && (
          <motion.div key="s0" initial={{opacity:0,x:20}} animate={{opacity:1,x:0}} exit={{opacity:0,x:-20}}>
            <Label className="text-white mb-3 block">What type of business?</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              {TYPES.map((t) => (
                <button key={t.v} data-testid={`estimate-type-${t.v}`} onClick={() => setSel({ ...sel, business_type: t.v })}
                  className={`px-3 py-3 rounded-xl text-sm font-medium border transition-all ${sel.business_type===t.v?"bg-violet-600 border-violet-500 text-white":"border-white/10 text-slate-300 hover:border-violet-500/50"}`}>{t.l}</button>
              ))}
            </div>
          </motion.div>
        )}
        {step === 1 && (
          <motion.div key="s1" initial={{opacity:0,x:20}} animate={{opacity:1,x:0}} exit={{opacity:0,x:-20}}>
            <Label className="text-white mb-4 block">How many pages? <span className="text-violet-400 font-mono">{sel.pages}</span></Label>
            <Slider data-testid="estimate-pages-slider" value={[sel.pages]} min={1} max={20} step={1} onValueChange={(v)=>setSel({...sel,pages:v[0]})} className="my-6" />
            <div className="flex justify-between text-xs text-slate-500"><span>1 page</span><span>20 pages</span></div>
          </motion.div>
        )}
        {step === 2 && (
          <motion.div key="s2" initial={{opacity:0,x:20}} animate={{opacity:1,x:0}} exit={{opacity:0,x:-20}}>
            <Label className="text-white mb-3 block">Which features do you need?</Label>
            <div className="space-y-2.5">
              {FEATURES.map((f) => (
                <label key={f.k} data-testid={`estimate-feature-${f.k}`} className="flex items-center gap-3 p-3 rounded-xl border border-white/10 cursor-pointer hover:border-violet-500/40">
                  <Checkbox checked={sel[f.k]} onCheckedChange={(c)=>setSel({...sel,[f.k]:!!c})} />
                  <span className="text-slate-200 text-sm">{f.l}</span>
                </label>
              ))}
            </div>
          </motion.div>
        )}
        {step === 3 && result && (
          <motion.div key="s3" initial={{opacity:0,scale:0.97}} animate={{opacity:1,scale:1}}>
            <div className="space-y-2 mb-4">
              {result.breakdown.map((b,i)=>(
                <div key={i} className="flex justify-between text-sm text-slate-300"><span>{b.label}</span><span className="font-mono">{rupee(b.amount)}</span></div>
              ))}
              {result.maintenance_monthly_rupees>0 && <div className="flex justify-between text-sm text-cyan-300"><span>Maintenance (monthly)</span><span className="font-mono">{rupee(result.maintenance_monthly_rupees)}/mo</span></div>}
            </div>
            <div className="flex justify-between items-center py-4 border-t border-white/10">
              <span className="text-white font-semibold">Estimated total</span>
              <span data-testid="estimate-total" className="font-display text-3xl font-bold text-gradient">{rupee(result.total_rupees)}</span>
            </div>
            <p className="text-xs text-amber-300/90 bg-amber-500/10 rounded-lg px-3 py-2 mb-5">{result.disclaimer}</p>

            {submitted ? (
              <div className="flex items-center gap-2 text-emerald-300 bg-emerald-500/10 rounded-xl px-4 py-3" data-testid="estimate-success">
                <Check className="w-5 h-5" /> Enquiry received. We'll confirm your quote soon.
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                <Input data-testid="estimate-lead-name" placeholder="Your name" value={lead.name} onChange={(e)=>setLead({...lead,name:e.target.value})} className="bg-white/5 border-white/10 text-white" />
                <Input data-testid="estimate-lead-phone" placeholder="Phone" value={lead.phone} onChange={(e)=>setLead({...lead,phone:e.target.value})} className="bg-white/5 border-white/10 text-white" />
                <label className="sm:col-span-2 flex items-center gap-2 text-sm text-slate-300">
                  <Checkbox checked={lead.consent} onCheckedChange={(c)=>setLead({...lead,consent:!!c})} data-testid="estimate-consent" /> I consent to be contacted about this enquiry.
                </label>
                <Button data-testid="estimate-calculator-submit" className="sm:col-span-2 bg-violet-600 hover:bg-violet-500 text-white font-semibold" onClick={submitLead} disabled={!lead.name||!lead.phone}>
                  Submit as Enquiry
                </Button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {step < 3 && (
        <div className="flex justify-between mt-8">
          <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-white/5" disabled={step===0} onClick={()=>setStep(step-1)}>Back</Button>
          {step < 2 ? (
            <Button data-testid="estimate-next" className="bg-violet-600 hover:bg-violet-500 text-white" onClick={()=>setStep(step+1)}>Next</Button>
          ) : (
            <Button data-testid="estimate-calculate" className="bg-violet-600 hover:bg-violet-500 text-white" onClick={calc} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "See Estimate"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
